/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import React, { useState, forwardRef, useCallback, useMemo, useId, useRef, useEffect } from "react";
import { untranslated, type TranslatedString } from "@i18n";
import {
  MenuItem,
  MenuList,
  ListItemText,
  SxProps,
  Theme,
  CircularProgress,
  Popper,
  ClickAwayListener,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { cssLengthToPx } from "@CubeComponents/utils/cssLengthToPx";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { AutoSizer, List } from "react-virtualized";
import TextFieldComponent from "./TextFieldComponent";
import type { RemoteOptionsReply } from "./remoteOptionsReply";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import Text from "@CubeComponents/html/Text/Text";
import ClippedTooltip from "@CubeComponents/html/tooltip/ClippedTooltip";
import * as Sentry from "@sentry/react";

// ===== Type Definitions =====

/** Base constraint for option objects - must be a plain object with string keys */
type OptionBase = Record<string, unknown>;

/**
 * Option members are `unknown` (an option is a `Record<string, unknown>`), so anything rendered out
 * of one has to be converted rather than asserted into `ReactNode`.
 */
function toDisplayNode(value: unknown): React.ReactNode {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "object" && React.isValidElement(value)) return value;
  return String(value);
}

/** React keys must be a string or a number; the `fields.value` member of an option is `unknown`. */
function toOptionKey(value: unknown, index: number): React.Key {
  if (typeof value === "string" || typeof value === "number") {
    return value || `item-${index}`;
  }
  return `item-${index}`;
}

/**
 * A selected option identified by its `fields.value` key alone, for callers that hold only the id
 * (e.g. it was persisted) and let the fetched `options` / `remoteDataSource` rows supply the label.
 */
type OptionValue = string | number;

/** Configuration for mapping option object keys to display text and unique value */
interface SelectFieldsConfig {
  text: string;
  value: string;
}

/** Individual where clause for remote data query filtering */
interface QueryWhereClause {
  isComplex?: boolean | undefined;
  ignoreCase?: boolean | undefined;
  ignoreAccent?: boolean | undefined;
  field?: string | undefined;
  operator?: string | undefined;
  value?: unknown;
}

/** Query parameters sent to remote data source */
interface RemoteDataQuery {
  where?: QueryWhereClause[] | undefined;
  [key: string]: unknown;
}

/** Arguments passed to beforeOpen callback in action menu mode */
interface BeforeOpenArgs {
  items: ActionMenuItem[];
}

/** Syncfusion-compatible item type for action menus */
interface ActionMenuItem {
  [key: string]: unknown;
  id?: string | undefined;
  text?: string | undefined;
  value?: string | number | undefined;
  iconCss?: string | undefined;
  separator?: boolean | undefined;
  disabled?: boolean | undefined;
}

/** Syncfusion-compatible event args for select callback */
interface SelectEventArgs {
  item: {
    properties: ActionMenuItem;
    id?: string | undefined;
    text?: string | undefined;
  };
  element?: HTMLElement | undefined;
  /** Set to true inside the callback to cancel the selection */
  cancel?: boolean | undefined;
}

/**
 * The four callbacks that take a `T` back from the component (`onChange`, `renderOption`,
 * `groupBy`, `valueTemplate`) are declared as METHODS, not as function-typed properties. Written as
 * properties they make `T` invariant, which says a `SelectDropDownProps<Row>` is unrelated to a
 * `SelectDropDownProps<OptionBase>` — and the rows those callbacks receive are the caller's own `T`
 * rows (action-menu `items` aside, see `asOption`), so the two instantiations ARE related. That
 * relation is what lets the public component below carry the generic that `forwardRef` erases.
 *
 * @typeParam Q - the request `value` `remoteDataSource` is called with, i.e. `query`'s own type.
 */
interface SelectDropDownProps<T extends OptionBase = OptionBase, Q extends RemoteDataQuery = RemoteDataQuery> {
  // Core data props
  options?: T[] | undefined;
  /** The selected option, or just its `fields.value` id — see `OptionValue`. */
  value?: T | T[] | OptionValue | null | undefined;
  onChange?(value: T | T[] | null, args?: { cancel: boolean }): void | Promise<void>;
  displayTemplate?: ((displayValue: React.ReactNode) => React.ReactNode) | undefined;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString | React.ReactNode | undefined;
  multiple?: boolean | undefined;
  isLoading?: boolean | undefined;
  disabled?: boolean | undefined;
  showNoOptions?: boolean | undefined;
  error?: string | undefined;
  /**
   * Remote option source in SlickGrid1's function form — a route caller from `postTo` (@AxiosWrapper) —
   * called with `{ value: query }`, plus a `startswith` `where` on the text field while a `searchable`
   * search is typed. A method for the same reason as the callbacks above; `NoInfer` keeps `Q` coming from
   * `query` and `T` from the props that render options, never from the route.
   */
  remoteDataSource?(body: { value: NoInfer<Q> }): Promise<RemoteOptionsReply<NoInfer<T>>>;
  /** The request `value` `remoteDataSource` is called with. */
  query?: Q | undefined;
  helperText?: string | undefined;
  size?: "small" | "medium" | "large" | undefined;
  maxHeight?: number | string | undefined;
  width?: string | number | undefined;
  sx?: SxProps<Theme> | undefined;
  menuSx?: SxProps<Theme> | undefined;
  buttonType?: string | undefined;
  persistPlaceholder?: boolean | undefined;
  fields?: SelectFieldsConfig | undefined;
  renderOption?(option: T, isSelected: boolean): React.ReactNode;
  startIcon?: React.ReactNode | undefined;

  clearable?: boolean | undefined;
  /**
   * Keeps a SINGLE-select menu open when `onChange` sets `args.cancel`, for a caller that refuses the
   * click (a permission check, a failed write) and wants the user to pick again without reopening.
   * Off by default: `cancel` has never suppressed the close here, only the uncontrolled label.
   */
  keepOpenOnCancel?: boolean | undefined;
  searchable?: boolean | undefined;
  virtualizeList?: boolean | undefined;
  groupBy?(option: T): string;
  buttonWidth?: string | undefined;
  headerTemplate?: (() => React.ReactNode) | undefined;
  footerTemplate?: (() => React.ReactNode) | undefined;
  emptyListTemplate?: (() => React.ReactNode) | undefined;

  id?: string | undefined;
  "aria-label"?: string | undefined;
  "aria-labelledby"?: string | undefined;
  popupAlign?: "left" | "right" | "center" | undefined;
  popperWidth?: number | string | undefined;
  disableClickAwayListener?: boolean | undefined;
  open?: boolean | undefined;
  onClose?: (() => void) | undefined;
  valueTemplate?(matchedOption: T | undefined): React.ReactNode;

  // Syncfusion DropDownButtonComponent compatibility props
  children?: React.ReactNode | undefined;
  items?: ActionMenuItem[] | undefined;
  select?: ((args: SelectEventArgs) => void) | undefined;
  onSelect?: ((args: SelectEventArgs) => void) | undefined;
  cssClass?: string | undefined;
  className?: string | undefined;
  hideCaretIcon?: boolean | undefined;
  content?: string | undefined;
  iconCss?: string | undefined;
  onClick?: ((e: React.MouseEvent) => void) | undefined;
  "data-testid"?: string | undefined;
  // Additional style props for compatibility
  style?: React.CSSProperties | undefined;
  bgColor?: string | undefined;
  // Legacy Syncfusion compatibility props
  allowButtonPadding?: boolean | undefined;
  popUpWidth?: string | undefined;
  maxPopUpHeight?: string | undefined;
  beforeOpen?: ((args: BeforeOpenArgs) => void) | undefined;
}

/**
 * Reports a caller that supplied neither `options`/`items` nor a remote source. Mounted only while that
 * is true, so the misuse is expressed by the render rather than re-tested inside an effect, and one
 * misconfigured mount produces one Sentry event instead of one per `options` identity. A caller mistake
 * is reported, never thrown: this component has a hundred-odd consumers and must not take a page down.
 *
 * ⚠️ The fingerprint is EXPLICIT because Sentry otherwise groups by the frames the Error was
 * constructed on: the identical message in AutoComplete.tsx would merge with this one, and any later
 * move of this reporter would split this issue in two again. Do not drop it, and do not share one
 * reporter between the two components — the two fingerprints are what keeps them separate issues.
 *
 * ⚠️ BUG: this fingerprint does not match the issue these events used to land in (that one grouped on
 * the stack of the effect that lived inside `SelectDropDownComponent`), so on deploy the pre-existing
 * "options should be an array or use remote data source" issue stops receiving events and every alert
 * rule, assignee, mute and regression-detection setting attached to it silently stops matching — a
 * muted caller misconfiguration starts paging, or a firing alert goes quiet. Needs the owner of the
 * Sentry project to close or remap the old issue onto `select-dropdown-missing-datasource`; it cannot
 * be fixed from this file.
 */
const MissingDataSourceReport = (): null => {
  useEffect(() => {
    const configError = new Error("options should be an array or use remote data source");
    console.log(configError);
    Sentry.captureException(configError, { fingerprint: ["select-dropdown-missing-datasource"] });
  }, []);
  return null;
};

function SelectDropDownComponent<T extends OptionBase = OptionBase, Q extends RemoteDataQuery = RemoteDataQuery>(
  props: SelectDropDownProps<T, Q>,
  ref: React.ForwardedRef<HTMLElement>,
): React.ReactElement {
  const {
    options = null,
    value,
    onChange,
    displayTemplate,
    placeholder,
    headerTemplate,
    footerTemplate,
    remoteDataSource,
    showNoOptions = true,
    query,
    multiple = false,
    isLoading = false,
    disabled = false,
    error,
    helperText,
    maxHeight: maxHeightProp,
    sx,
    buttonType,
    menuSx,
    renderOption,
    startIcon,
    clearable = false,
    keepOpenOnCancel = false,
    searchable = false,
    virtualizeList = false,
    groupBy,
    id,
    fields,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    buttonWidth,
    popupAlign = "left",
    popperWidth: popperWidthProp,
    disableClickAwayListener,
    open: controlledOpen,
    onClose,
    persistPlaceholder,
    valueTemplate,
    // Syncfusion compatibility props
    children,
    items,
    select,
    onSelect,
    cssClass = "",
    className = "",
    hideCaretIcon,
    // content,
    onClick,
    "data-testid": dataTestId,
    style,
    bgColor,
    // Legacy Syncfusion compatibility props
    popUpWidth,
    maxPopUpHeight,
    beforeOpen,
    emptyListTemplate,
  } = props;

  // Map legacy props to their correct counterparts
  const popperWidth = popperWidthProp || popUpWidth;
  const maxHeight =
    maxHeightProp ??
    (maxPopUpHeight
      ? Number.isFinite(cssLengthToPx(maxPopUpHeight))
        ? cssLengthToPx(maxPopUpHeight)
        : cssLengthToPx("18.75rem")
      : cssLengthToPx("18.75rem"));

  // Determine if this is being used as an action menu (Syncfusion-style)
  const isActionMenu = Boolean(children || items);

  const shouldHideCaret = hideCaretIcon || cssClass.includes("e-caret-hide");

  // Use items if provided, otherwise use options
  const effectiveOptions: OptionBase[] | null = items ?? options;

  // Default fields for action menu items

  const effectiveFields: SelectFieldsConfig = fields ?? { text: "text", value: "id" };
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const open = controlledOpen === undefined ? Boolean(anchorEl) : Boolean(anchorEl) && controlledOpen;
  const [optionsState, setOptionsState] = useState<OptionBase[]>(() =>
    Array.isArray(effectiveOptions) ? effectiveOptions : [],
  );
  const optionId = effectiveFields.value;
  const generatedId = useId();
  const uniqueId = useMemo(() => id || `select-dropdown-${generatedId}`, [id, generatedId]);
  const [fetching, setFetching] = useState<boolean>(false);
  const loading = isLoading || fetching;

  // A caller that closes the menu through the `open` prop never reaches `handleClose`, so the
  // internal `anchorEl` / `searchText` would otherwise survive the close and reopen with stale search
  // text.
  const [prevControlledOpen, setPrevControlledOpen] = useState(controlledOpen);
  if (!Object.is(prevControlledOpen, controlledOpen)) {
    setPrevControlledOpen(controlledOpen);
    if (controlledOpen === false) {
      setAnchorEl(null);
      setSearchText("");
    }
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Call custom onClick handler if provided
    if (onClick) {
      onClick(event);
    }

    if (!disabled) {
      if (anchorEl) {
        // Currently open, close it
        setAnchorEl(null);
      } else {
        // About to open - call beforeOpen callback to allow item modification
        if (beforeOpen && isActionMenu) {
          const itemsCopy = optionsState.map((item) => ({ ...item })) as ActionMenuItem[];
          beforeOpen({ items: itemsCopy });
          setOptionsState(itemsCopy);
        }
        setAnchorEl(event.currentTarget);
      }
    }
  };

  /**
   * An uncontrolled caller (`onChange` without a `value` prop) never sends the selection back, so
   * `handleSelect` latches the chosen label here. It is only ever written while `value` is falsy, and
   * it is dropped as soon as the caller takes over the value or re-seeds the option list the label
   * was resolved from.
   */
  const [uncontrolledLabel, setUncontrolledLabel] = useState<React.ReactNode>(null);

  // `optionsState` is also written by `fetchOptions` (remote rows) and by `beforeOpen` (action-menu
  // mode), so it is not a pure function of `effectiveOptions`.
  const [prevEffectiveOptions, setPrevEffectiveOptions] = useState(effectiveOptions);
  if (!Object.is(prevEffectiveOptions, effectiveOptions)) {
    setPrevEffectiveOptions(effectiveOptions);
    setUncontrolledLabel(null);
    if (Array.isArray(effectiveOptions)) {
      setOptionsState(effectiveOptions);
    }
  }

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ⚠️ Concurrent calls (a search keystroke while the first request is still open) share `fetching`
  // and `optionsState`: the response that lands last wins, and the first `finally` to run hides the
  // spinner while a later request is still in flight.
  const fetchOptions = async (queryParam: Q | undefined = query) => {
    if (!queryParam || !remoteDataSource) return;
    setFetching(true);
    try {
      const { data } = await remoteDataSource({ value: queryParam });
      if (!mountedRef.current) return;
      setOptionsState(data.status ? (data.result ?? []) : []);
    } finally {
      // The rejection itself is deliberately left to propagate: `loading` is the only state this
      // component can repair, and swallowing the error here would lose the Sentry report that the
      // resulting unhandled rejection produces.
      setFetching(false);
    }
  };

  useEffect(() => {
    // Fire-and-forget from an effect; `fetchOptions` drives its own in-flight flag.
    // ⚠️ A rejected request keeps the previous option list and is reported only as an unhandled
    // rejection; nothing tells the user the list is stale.
    void fetchOptions();
  }, [query]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
    onClose?.();
  };

  // A grid cell's React tree outlives its DOM (cellPortals.tsx sweeps on a 2s grace), so a Popper left
  // anchored to a detached node renders at the page's top-left. Close as soon as the anchor goes.
  useEffect(() => {
    if (!open || !anchorEl) return undefined;
    const closeIfAnchorDetached = () => {
      if (anchorEl.isConnected) return;
      setAnchorEl(null);
      setSearchText("");
      onClose?.();
    };
    const observer = new MutationObserver(closeIfAnchorDetached);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
    };
  }, [open, anchorEl, onClose]);

  const standbyRef = useRef<HTMLElement | null>(null);

  const buttonRef = ref ?? standbyRef;

  // The forwarded ref is declared over `HTMLElement` because it is attached to several different
  // slots; a callback ref hands the concrete node to whichever ref shape the caller passed instead
  // of asserting the narrow element type onto the wide one (same pattern as MenuItemComponent).
  const setActionButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (typeof buttonRef === "function") {
        buttonRef(node);
      } else {
        buttonRef.current = node;
      }
    },
    [buttonRef],
  );

  /**
   * `optionsState` holds `OptionBase` rows because it is fed from either `props.options` (`T[]`),
   * `props.remoteDataSource` (`T[]`) or the action-menu `props.items` (`ActionMenuItem[]`).
   * `onChange` never receives an `items` row, but `renderOption`, `groupBy` and `valueTemplate` also
   * run in action-menu mode, so there the assertion holds only while the `items` rows are the
   * caller's `T`. Every such caller today satisfies it: CommentThread (`renderOption`), Estimate and
   * CostDatabase (`valueTemplate`) leave `T` at `OptionBase`; LayoutRow and SingleTransmittal
   * (`renderOption`) set `T` to their `items` element type.
   * ⚠️ Nothing enforces this: `items` is `ActionMenuItem[]`, not `T[]`, so an `items` menu can declare
   * a `T` its rows do not satisfy, and these callbacks then receive those rows typed as that `T`.
   */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see above: rows are the caller's own `T[]`, except action-menu `items`, which are `T` only while the caller leaves `T` at `OptionBase` or sets it to the `items` element type
  const asOption = (option: OptionBase): T => option as T;

  const handleSelect = async (option: OptionBase, element?: HTMLElement) => {
    if (option.disabled) return;

    // For action menu mode (Syncfusion compatibility)
    if (isActionMenu) {
      const menuItem = option as ActionMenuItem;
      const selectArgs: SelectEventArgs = {
        item: {
          properties: menuItem,
          id: menuItem.id,
          text: menuItem.text,
        },
        element,
        cancel: false,
      };

      // Call Syncfusion-compatible callbacks - either may set cancel=true to abort
      if (select) {
        select(selectArgs);
      }
      if (onSelect) {
        onSelect(selectArgs);
      }

      if (selectArgs.cancel) return;

      handleClose();
      return;
    }

    // Standard select dropdown mode
    if (multiple) {
      const currentValues: T[] = Array.isArray(value) ? value : [];
      const alreadySelected = currentValues.some((v) => v[optionId] === option[optionId]);
      const newValue: T[] = alreadySelected
        ? currentValues.filter((v) => v[optionId] !== option[optionId])
        : [...currentValues, asOption(option)];
      const changeArgs = { cancel: false };
      if (onChange) await onChange(newValue, changeArgs);
      if (changeArgs.cancel) return;
    } else {
      const changeArgs = { cancel: false };
      if (onChange) await onChange(asOption(option), changeArgs);
      /**
       * OPT-IN, not the default: a single-select menu has always closed on every click, `cancel` or
       * not, and the two callers that raise it today (ProjectUsers, ExistingPortalUsers) rely on
       * that. Only a caller that asks for it keeps the menu open — see `keepOpenOnCancel`.
       */
      if (changeArgs.cancel && keepOpenOnCancel) return;
      if (!value && !changeArgs.cancel && !persistPlaceholder) {
        const text = option[effectiveFields.text];
        if (text != null) {
          setUncontrolledLabel(toDisplayNode(text));
        }
      }
      handleClose();
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    const changeArgs = { cancel: false };
    const cleared: T[] = [];
    // Fire-and-forget: the clear button's handler is synchronous and reads nothing back. (The two
    // `onChange` calls in `handleSelect` await it because the display value is set afterwards.)
    if (onChange) void onChange(multiple ? cleared : null, changeArgs);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    // A search narrows the source's own request, so like the first fetch it needs `query`.
    if (remoteDataSource && query) {
      const searchQuery: Q = {
        ...query,
        where: [
          {
            isComplex: false,
            ignoreCase: true,
            ignoreAccent: false,
            field: effectiveFields.text,
            operator: "startswith",
            value: event.target.value,
          },
        ],
      };
      // Fire-and-forget: the search box stays responsive and the list re-renders when it lands.
      void fetchOptions(searchQuery);
    }
  };

  /** The selected option's id, whether `value` is the whole option or just the id itself. */
  const selectedOptionId = (selected: T | OptionValue) =>
    typeof selected === "object" ? selected[optionId] : selected;

  const isSelected = useCallback(
    (option: OptionBase) => {
      if (!value) return false;
      if (Array.isArray(value)) {
        return value.some((v) => v[optionId] === option[optionId]);
      }
      return selectedOptionId(value) === option[optionId];
    },
    [value, optionId],
  );

  const translatedPlaceholder: React.ReactNode = placeholder || t("Select an option");

  // Several callers compute `value` inline (`value={rows.filter(...)}`), so its identity changes on
  // every parent render; latching on whether a selection exists keeps that churn out of the render
  // phase, and is enough because `uncontrolledLabel` is only ever written while `value` is falsy.
  const hasValue = Boolean(value);
  const [prevHasValue, setPrevHasValue] = useState(hasValue);
  if (!Object.is(prevHasValue, hasValue)) {
    setPrevHasValue(hasValue);
    setUncontrolledLabel(null);
  }

  const resolveDisplayValue = (): React.ReactNode => {
    // An action menu's trigger is its `children`, and `persistPlaceholder` asks for the placeholder
    // to survive selection; neither ever shows a resolved label.
    if (isActionMenu || persistPlaceholder) return translatedPlaceholder;
    if (!value || !optionsState.length) return uncontrolledLabel ?? translatedPlaceholder;

    if (Array.isArray(value)) {
      const names = value
        .map((v) => v[effectiveFields.text])
        .filter(Boolean)
        .join(", ");
      return names || translatedPlaceholder;
    }
    // Only a whole option carries the label; an id-only `value` falls straight through to the
    // lookup below, which resolves the label from the fetched rows.
    const text = typeof value === "object" ? value[effectiveFields.text] : undefined;
    if (text != null) return toDisplayNode(text);
    const matched = optionsState.find((option) => option[optionId] === selectedOptionId(value));
    return toDisplayNode(matched?.[effectiveFields.text]) || translatedPlaceholder;
  };

  const displayValue = resolveDisplayValue();

  const filteredOptions = useMemo(() => {
    // Filter out separator items for processing (they're rendered separately)
    const nonSeparatorOptions = optionsState.filter((option) => !option.separator);
    if (!searchText || Boolean(remoteDataSource)) return nonSeparatorOptions;
    return nonSeparatorOptions.filter((option) => {
      const optionText = option[effectiveFields.text];
      if (typeof optionText === "string") {
        const lowerSearchText = searchText.toLowerCase();
        return optionText.toLowerCase().includes(lowerSearchText);
      } else {
        return optionText === searchText;
      }
    });
  }, [optionsState, searchText, effectiveFields.text, remoteDataSource]);

  const groupedOptions = useMemo(() => {
    if (!groupBy) return { ungrouped: filteredOptions };

    return filteredOptions.reduce(
      (acc: Record<string, OptionBase[]>, option) => {
        const groupKey = String(option.group || groupBy(asOption(option)) || "ungrouped");
        acc[groupKey] ??= [];
        acc[groupKey].push(option);
        return acc;
      },
      {} as Record<string, OptionBase[]>,
    );
  }, [filteredOptions, groupBy]);

  const renderMenuItem = useCallback(
    (option: OptionBase, index: number, style?: React.CSSProperties) => {
      // Handle separator items
      if (option.separator) {
        return <Divider key={`separator-${index}`} sx={{ my: "0.25rem" }} />;
      }

      const itemSelected = isSelected(option);

      if (renderOption) {
        return (
          <MenuItemComponent
            key={toOptionKey(option[optionId], index)}
            selected={itemSelected}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              // Fire-and-forget: the click handler has a void contract and `handleSelect` owns the
              // whole selection sequence, including closing the menu.
              void handleSelect(option, e.currentTarget);
            }}
            disabled={Boolean(option.disabled)}
            style={style}
            sx={{ opacity: option.disabled ? 0.5 : 1 }}
          >
            {renderOption(asOption(option), itemSelected)}
          </MenuItemComponent>
        );
      }

      return (
        <MenuItemComponent
          key={toOptionKey(option[optionId], index)}
          selected={itemSelected}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            // Fire-and-forget, as in the `renderOption` branch above.
            void handleSelect(option, e.currentTarget);
          }}
          disabled={Boolean(option.disabled)}
          style={style}
          sx={{ opacity: option.disabled ? 0.5 : 1 }}
        >
          {multiple && <CheckBoxComponent checked={itemSelected} disabled={Boolean(option.disabled)} />}
          <ListItemText
            primary={
              /* CUBE-28079: the tooltip must wrap the CLIPPING element, not sit inside it. MUI
                 Tooltip wraps a non-element child in its own inline <span> and uses that as the
                 Popper anchor (Tooltip.js: `isValidElement(childrenProp) ? childrenProp : <span>`).
                 That auto-span sat inside the noWrap Typography, so - overflow being a paint-time
                 clip - its getBoundingClientRect() returned the FULL unclipped text width. The
                 tooltip was therefore centred on text extending past the visible option, displaced
                 by roughly half the hidden overflow: correct for a short option, pushed clear off
                 the dropdown for a long one. Anchoring on the Typography makes the anchor rect the
                 visible box, so placement no longer depends on the option's length. */
              <ClippedTooltip content={untranslated(String(option[effectiveFields.text] ?? ""))}>
                <Typography
                  noWrap
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {toDisplayNode(option[effectiveFields.text])}
                </Typography>
              </ClippedTooltip>
            }
          />
        </MenuItemComponent>
      );
    },
    [multiple, isSelected, renderOption, effectiveFields.text, handleSelect, optionId],
  );

  const getPlacement = () => {
    if (popupAlign === "left") return "bottom-start";
    if (popupAlign === "right") return "bottom-end";
    return "bottom";
  };

  const getVirtualListHeight = () => {
    if (typeof maxHeight === "number") {
      const searchBarHeight = searchable ? 70 : 0;
      return maxHeight - searchBarHeight;
    }
    return 250;
  };

  const renderMenuContent = () => {
    if (loading) {
      return (
        <MenuItem sx={{ paddingBottom: "1.25rem" }} disabled>
          <CircularProgress size={20} style={{ marginInlineEnd: "0.5rem" }} />
          {t("Loading...")}
        </MenuItem>
      );
    }

    if (filteredOptions.length === 0) {
      if (emptyListTemplate) {
        return emptyListTemplate();
      }
      return (
        showNoOptions && (
          <MenuItem disabled>
            <Text>{t("No options available")}</Text>
          </MenuItem>
        )
      );
    }

    if (virtualizeList) {
      return (
        <div style={{ height: getVirtualListHeight() }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                width={width}
                rowCount={filteredOptions.length}
                rowHeight={48}
                rowRenderer={({ index, style }) => renderMenuItem(filteredOptions[index]!, index, style)}
              />
            )}
          </AutoSizer>
        </div>
      );
    }

    if (groupBy) {
      return Object.entries(groupedOptions).map(([group, groupOptions]) => (
        <React.Fragment key={group}>
          {group !== "ungrouped" && (
            <MenuItem
              disabled
              style={{
                fontWeight: "bold",
                opacity: 0.7,
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              }}
            >
              {group}
            </MenuItem>
          )}
          {groupOptions.map((option, index) => renderMenuItem(option, index))}
        </React.Fragment>
      ));
    }

    // For action menus, render items including separators in original order
    if (isActionMenu) {
      return optionsState.map((option, index) => renderMenuItem(option, index));
    }

    return filteredOptions.map((option, index) => renderMenuItem(option, index));
  };

  // The row handed to `valueTemplate`: `value` is either the whole option or just its `fields.value`
  // id, and a multi-select array has no single row to template.
  const matchedValue =
    value == null || Array.isArray(value)
      ? undefined
      : optionsState.find((option) => option[optionId] === selectedOptionId(value));
  const matchedValueOption = matchedValue ? asOption(matchedValue) : undefined;

  // Combine cssClass and className for action menu compatibility
  const combinedClassName = `${cssClass} ${className}`.trim();

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (!disableClickAwayListener) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          width: isActionMenu ? "auto" : buttonWidth ? buttonWidth : "100%",
          backgroundColor: bgColor,
          ...style,
        }}
        className={combinedClassName}
        data-testid={dataTestId}
      >
        {/* Action menus may start with no items; other modes need `options`/`items` or a remote source. */}
        {Array.isArray(effectiveOptions) || remoteDataSource || query || isActionMenu ? null : (
          <MissingDataSourceReport />
        )}
        {/* Action menu mode: children as trigger */}
        {isActionMenu && children ? (
          <button
            type="button"
            onClick={handleOpen}
            disabled={disabled}
            className="flex items-center justify-center cursor-pointer bg-transparent border-none p-0"
            style={{ opacity: disabled ? 0.5 : 1 }}
            ref={setActionButtonRef}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? `${uniqueId}-menu` : undefined}
          >
            {children}
          </button>
        ) : displayTemplate ? (
          <button className="w-full h-full" onClick={handleOpen}>
            {" "}
            {displayTemplate(displayValue)}{" "}
          </button>
        ) : (
          <ButtonComponent
            onClick={handleOpen}
            buttonType={buttonType}
            disabled={disabled}
            sx={{
              textAlign: "left",
              width: buttonWidth ? buttonWidth : "100%",
              justifyContent: "space-between",
              maxWidth: buttonWidth ? buttonWidth : "100%",
              minWidth: "0 !important",
              "& > span.font-semibold": {
                overflow: "hidden",
                minWidth: 0,
                flex: "1 1 0",
              },
              ...(sx ?? {}),
            }}
            // @ts-expect-error -- an `HTMLElement` ref is forwarded to a MUI slot typed `Ref<HTMLButtonElement>`. Same element-type mismatch as `ToggleSwitchComponent`; the ref is created generically and used for several slots.
            ref={buttonRef}
            startIcon={startIcon}
            endIcon={
              shouldHideCaret ? null : (
                <>
                  {clearable && value && (!Array.isArray(value) || value.length !== 0) && (
                    <IconButton sx={{ padding: "0.125rem" }} onClick={handleClear}>
                      <CloseIcon
                        fontSize="inherit"
                        style={{ marginInlineEnd: 0, cursor: "pointer", alignSelf: "center", height: "100%" }}
                      />
                    </IconButton>
                  )}
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </>
              )
            }
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? `${uniqueId}-menu` : undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            id={uniqueId}
          >
            {loading ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={20} style={{ marginInlineEnd: "0.5rem" }} />
                {t("Loading...")}
              </div>
            ) : valueTemplate ? (
              valueTemplate(matchedValueOption)
            ) : (
              displayValue
            )}
          </ButtonComponent>
        )}

        <Popper
          id={`${uniqueId}-popper`}
          open={open}
          anchorEl={anchorEl}
          sx={{
            // For action menus, use auto width; for regular dropdowns, match button width
            width: isActionMenu ? "auto" : popperWidth || buttonWidth || "auto",
            maxWidth: isActionMenu ? "none" : popperWidth || buttonWidth || "auto",
            minWidth: isActionMenu ? "9.375rem" : popperWidth || buttonWidth || "auto",
            border: "1px solid #ECF3FE",
            boxShadow: "0.25rem 0.25rem 0.9375rem rgba(65, 92, 171, 0.15)",
            zIndex: 1500,
            background: "var(--surface-white)",
            maxHeight: `${Number(maxHeight) / 16}rem`,
            overflow: "auto",
            ...(menuSx ?? {}),
          }}
          placement={getPlacement()}
          modifiers={[
            {
              name: "sameWidth",
              // Disable sameWidth modifier for action menus
              enabled: !isActionMenu,
              phase: "beforeWrite",
              requires: ["computeStyles"],
              fn: ({ state }) => {
                // popperWidth may be a number, "Npx", or "Nrem"; normalize to px for popper.
                const resolved = cssLengthToPx(popperWidth);
                const widthPx = Number.isFinite(resolved) ? resolved : state.rects.reference.width;
                // eslint-disable-next-line no-px/no-px -- popper width must match reference element's DOM-measured px width exactly; rem conversion risks subpixel drift if root font-size != 16px
                state.styles.popper = { ...(state.styles.popper ?? {}), width: `${widthPx}px` };
              },
            },
          ]}
        >
          <>
            {headerTemplate && <div className="py-4 sticky flex top-0 w-full z-10 bg-white">{headerTemplate()}</div>}
            {searchable && (
              <div style={{ padding: "1rem", position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                <TextFieldComponent
                  placeholder={t("Search...")}
                  size="small"
                  value={searchText}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </div>
            )}

            {/* MenuList provides the MenuListContext + RovingTabIndexContext that MUI v9's
                MenuItem now *requires* (it throws without them). The search field / header /
                footer stay outside so MenuList typeahead & arrow-key nav don't hijack the
                search input. disablePadding keeps spacing identical to the pre-wrap layout. */}
            <MenuList disablePadding sx={{ outline: "none" }}>
              {renderMenuContent()}
            </MenuList>
            {footerTemplate && <div className="py-4 flex sticky bottom-0 w-full bg-white z-10">{footerTemplate()}</div>}
          </>
        </Popper>

        {(error || helperText) && (
          <div
            style={{
              color: error ? "red" : "rgba(0, 0, 0, 0.6)",
              marginTop: "0.25rem",
              fontSize: "0.75rem",
            }}
          >
            {error || helperText}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}

/**
 * `forwardRef` erases the render function's type parameters, so the public component is declared with
 * the generic call signature it actually has and the erased result is assigned into it. The return
 * is `ReactNode` because that is what the erased `ForwardRefExoticComponent` call signature returns,
 * not the narrower `ReactElement | null` the render function happens to produce.
 */
interface SelectDropDownType {
  <T extends OptionBase = OptionBase, Q extends RemoteDataQuery = RemoteDataQuery>(
    props: SelectDropDownProps<T, Q> & React.RefAttributes<HTMLElement>,
  ): React.ReactNode;
  displayName?: string | undefined;
}

const SelectDropDown: SelectDropDownType = forwardRef(SelectDropDownComponent);

SelectDropDown.displayName = "SelectDropDown";
export default SelectDropDown;
export type {
  SelectDropDownProps,
  ActionMenuItem,
  SelectEventArgs,
  SelectFieldsConfig,
  RemoteDataQuery,
  BeforeOpenArgs,
  OptionBase,
  QueryWhereClause,
};
